'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useDisabledState } from '../../hooks/use-disabled-state';
import { Slot } from '../slot';
import { anchorRecipe } from './styles/anchor.styles';
import type { AnchorPropsInterface } from './types/anchor.types';

/**
 * @since 6.0.0
 */

export const Anchor = forwardRef<HTMLAnchorElement, AnchorPropsInterface>(
  (props: AnchorPropsInterface, forwardedRef) => {
    const {
      onImpact,
      asChild = false,
      className,
      css: cssProp,
      disabled = false,
      href,
      ...rest
    } = props;

    const variants = splitResponsiveVariant('onImpact', onImpact);

    const styles = css(anchorRecipe.raw({ ...variants, disabled }), cssProp);

    const Component = asChild ? Slot : 'a';

    const a11yDisabledState = useDisabledState({ disabled, focusable: true });

    // Remove the `href` if disabled (CDS-3388)
    const disabledAnchor = useMemo(() => {
      if (disabled && Component === 'a') {
        return { href: undefined, role: 'link' };
      }
      return { href };
    }, [disabled, Component, href]);

    return (
      <Component
        ref={forwardedRef}
        data-c11n-component="Anchor"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n-semantic', styles, className)}
        {...rest}
        {...disabledAnchor}
        {...a11yDisabledState}
      />
    );
  },
);

Anchor.displayName = 'Anchor';
